import { useEffect } from "react"
import { Platform } from "react-native"
import * as Notifications from "expo-notifications"
import * as WebBrowser from "expo-web-browser"
import { setIchioshiUrl } from "src/storage/ichioshiStorage"

let isProcessing = false
const processedNotificationIds = new Set<string>()

export const useIOSWebViewHandler = (lastNotificationResponse?: Notifications.NotificationResponse | null) => {
  // iOS通知権限リクエスト（Expo Notificationsを使用）
  useEffect(() => {
    if (Platform.OS !== "ios") {
      return
    }

    Notifications.requestPermissionsAsync()
      .then((status) => {
        if (status.status !== "granted") {
          console.warn("[IOSWebViewHandler] Notification permission denied by user")
        }
        // APNsトークンの取得
        return Notifications.getDevicePushTokenAsync()
      })
      .then(() => {
        // APNsトークン取得完了
      })
      .catch((error) => {
        console.error("[IOSWebViewHandler] Failed to request iOS permission:", error)
      })
  }, [])

  // iOS通知レスポンス処理（アプリ起動時）
  useEffect(() => {
    if (Platform.OS !== "ios" || !lastNotificationResponse) {
      return
    }

    handleNotificationResponse(lastNotificationResponse)
  }, [lastNotificationResponse])

  // iOS通知レスポンス処理（リスナー）
  useEffect(() => {
    if (Platform.OS !== "ios") {
      return
    }

    const subscription = Notifications.addNotificationResponseReceivedListener((response) => {
      handleNotificationResponse(response)
    })

    return () => {
      subscription.remove()
    }
  }, [])
}

const handleNotificationResponse = (response: Notifications.NotificationResponse) => {
  if (Platform.OS !== "ios") {
    return
  }

  // WebView用のURLのみを処理（urlSchemeは処理しない）
  const url = extractUrlFromNotification(response)
  if (!url) {
    // URLがない場合はLinkingConfigurationで処理される
    return
  }

  if (!isValidWebUrl(url)) {
    // Web URL以外はLinkingConfigurationで処理される
    return
  }

  const notificationId = response?.notification?.request?.identifier

  if (notificationId && processedNotificationIds.has(notificationId)) {
    return
  }

  if (notificationId) {
    processedNotificationIds.add(notificationId)
    if (processedNotificationIds.size > 10) {
      const firstId = processedNotificationIds.values().next().value
      if (firstId) {
        processedNotificationIds.delete(firstId)
      }
    }
  }

  setTimeout(() => {
    openWebView(url)
  }, 2000)
}

const extractUrlFromNotification = (response: Notifications.NotificationResponse): string | null => {
  const trigger = response?.notification?.request?.trigger as Record<string, any>
  const content = response?.notification?.request?.content

  if (Platform.OS === "ios") {
    const payloadUrl = trigger?.payload?.url
    if (payloadUrl) {
      return payloadUrl
    }

    const contentUrl = content?.data?.url as string
    if (contentUrl) {
      return contentUrl
    }
  }

  return null
}

const isValidWebUrl = (url: string): boolean => {
  return url.startsWith("http://") || url.startsWith("https://")
}

const openWebView = async (url: string) => {
  if (isProcessing) {
    return
  }

  isProcessing = true

  try {
    await setIchioshiUrl(url)
  } catch (error) {
    console.error("[IOSWebViewHandler] Failed to save ichioshi data:", error)
  }

  WebBrowser.openBrowserAsync(url).catch(() => {
    // WebView open error is ignored
  })

  setTimeout(() => {
    isProcessing = false
  }, 5000)
}
